const MODAL_SELECTOR = '#df_media_modal';
const CONTENT_SELECTOR = '.df-media-modal-content';
const ROTATE_BUTTONS_SELECTOR = '.df-media-modal-rotate-buttons';
const ROTATE_SPINNER_SELECTOR = '.df-media-modal-rotate-spinner';
let dfMediaModalEventsBound = false;
let currentDfMediaModalTrigger = null;

const mediaItemsFromTrigger = (trigger) => {
    try {
        return JSON.parse(trigger.dataset.dfMediaItems || '[]');
    } catch (error) {
        return [];
    }
};

const imageElement = (item) => {
    const image = document.createElement('img');
    image.src = item.src;
    image.className = 'img-fluid w-100';
    if (item.alt) image.alt = item.alt;
    return image;
};

const videoElement = (item) => {
    const video = document.createElement('video');
    video.className = 'w-100';
    video.controls = true;
    video.playsInline = true;
    if (item.poster) video.poster = item.poster;

    const sources = item.sources?.length ? item.sources : [{ src: item.src, type: item.mimeType }];
    sources.forEach((source) => {
        if (!source.src) return;

        const element = document.createElement('source');
        element.src = source.src;
        if (source.type) element.type = source.type;
        video.appendChild(element);
    });

    return video;
};

const mediaElement = (item) => (item.type === 'video' ? videoElement(item) : imageElement(item));

const sliderElement = (items, activeIndex = 0) => {
    const carouselId = 'df_media_modal_carousel';
    const carousel = document.createElement('div');
    carousel.id = carouselId;
    carousel.className = 'carousel slide';
    carousel.dataset.bsRide = 'false';

    const inner = document.createElement('div');
    inner.className = 'carousel-inner';

    items.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = `carousel-item${index === activeIndex ? ' active' : ''}`;
        slide.appendChild(mediaElement(item));
        inner.appendChild(slide);
    });

    carousel.appendChild(inner);

    if (items.length > 1) {
        carousel.insertAdjacentHTML('beforeend',
            `<button class="btn apl-glas-effect carousel-control-prev rounded-circle" type="button" data-bs-target="#${carouselId}" data-bs-slide="prev">
                <i class="cww-icons ico-chevron-left fs-title-0" aria-hidden="true"></i>
                <span class="visually-hidden">Previous</span>
            </button>
            <button class="btn apl-glas-effect carousel-control-next rounded-circle" type="button" data-bs-target="#${carouselId}" data-bs-slide="next">
                <i class="cww-icons ico-chevron-right fs-title-0" aria-hidden="true"></i>
                <span class="visually-hidden">Next</span>
            </button>`
        );
    }

    return carousel;
};

const TRIGGER_SELECTOR = '[data-bs-target="#df_media_modal"][data-df-media-items]';
const GROUP_SELECTOR = '.files, .fb-element-dynamic-units, .form-row';
const groupedMediaItems = (trigger) => {
    const group = trigger.closest(GROUP_SELECTOR);
    const triggers = group ? Array.from(group.querySelectorAll(TRIGGER_SELECTOR)) : [trigger];

    const items = [];
    let activeIndex = 0;
    triggers.forEach((groupTrigger) => {
        const triggerItems = mediaItemsFromTrigger(groupTrigger);
        if (!triggerItems.length) return;
        if (groupTrigger === trigger) activeIndex = items.length;
        items.push(...triggerItems);
    });

    if (!items.length) {
        const fallback = mediaItemsFromTrigger(trigger);
        return { items: fallback, activeIndex: 0 };
    }

    return { items, activeIndex };
};

const renderDfMediaModal = (modal, trigger) => {
    const contentContainer = modal.querySelector(CONTENT_SELECTOR);
    if (!contentContainer) return;

    const { items, activeIndex } = trigger ? groupedMediaItems(trigger) : { items: [], activeIndex: 0 };

    contentContainer.replaceChildren();
    if (items.length === 0) return;

    contentContainer.appendChild(items.length === 1 ? mediaElement(items[0]) : sliderElement(items, activeIndex));
};

const activeModalMediaTrigger = (modal) => {
    if (!modal || !currentDfMediaModalTrigger) return null;

    const carousel = modal.querySelector('#df_media_modal_carousel');
    if (!carousel) return currentDfMediaModalTrigger;

    const activeItem = carousel.querySelector('.carousel-item.active');
    const activeIndex = Array.from(carousel.querySelectorAll('.carousel-item')).indexOf(activeItem);
    const group = currentDfMediaModalTrigger.closest(GROUP_SELECTOR);
    const triggers = group ? Array.from(group.querySelectorAll(TRIGGER_SELECTOR)) : [currentDfMediaModalTrigger];

    return triggers[activeIndex] || currentDfMediaModalTrigger;
};

const mediaTriggerCanRotate = (trigger) => (
    !!trigger
    && !!trigger.closest('.parent-block')?.querySelector('.rotate-buttons')
    && ['image', 'video'].includes(trigger.dataset.dfMediaType)
);

const updateModalRotateControls = (modal) => {
    const rotateButtons = modal.querySelector(ROTATE_BUTTONS_SELECTOR);
    const rotateSpinner = modal.querySelector(ROTATE_SPINNER_SELECTOR);
    if (!rotateButtons) return;

    const isRotating = modal.dataset.dfMediaModalRotating === 'true';
    rotateButtons.classList.toggle('d-none', isRotating || !mediaTriggerCanRotate(activeModalMediaTrigger(modal)));
    rotateButtons.querySelectorAll('button').forEach((button) => {
        button.disabled = isRotating;
    });
    if (rotateSpinner) rotateSpinner.classList.toggle('d-none', !isRotating);
};

const setDfMediaModalRotating = (modal, isRotating) => {
    if (!modal) return;

    modal.dataset.dfMediaModalRotating = isRotating ? 'true' : 'false';
    updateModalRotateControls(modal);
};

const refreshDfMediaModalFromTrigger = (trigger) => {
    const modal = document.querySelector(MODAL_SELECTOR);
    if (!modal || !modal.classList.contains('show')) return;

    currentDfMediaModalTrigger = trigger || currentDfMediaModalTrigger;
    renderDfMediaModal(modal, currentDfMediaModalTrigger);
    setDfMediaModalRotating(modal, false);
    updateModalRotateControls(modal);
};

const clearDfMediaModal = (modal) => {
    const contentContainer = modal.querySelector(CONTENT_SELECTOR);
    if (!contentContainer) return;

    contentContainer.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
    });
    contentContainer.replaceChildren();
    currentDfMediaModalTrigger = null;
};

const bindDfMediaModalEvents = () => {
    if (dfMediaModalEventsBound || window.dfMediaModalEventsBound) return;
    dfMediaModalEventsBound = true;
    window.dfMediaModalEventsBound = true;

    document.addEventListener('show.bs.modal', (event) => {
        if (!event.target.matches(MODAL_SELECTOR)) return;
        setDfMediaModalRotating(event.target, false);
        currentDfMediaModalTrigger = event.relatedTarget;
        renderDfMediaModal(event.target, currentDfMediaModalTrigger);
        updateModalRotateControls(event.target);
    });

    document.addEventListener('hidden.bs.modal', (event) => {
        if (!event.target.matches(MODAL_SELECTOR)) return;
        setDfMediaModalRotating(event.target, false);
        clearDfMediaModal(event.target);
    });

    document.addEventListener('slid.bs.carousel', (event) => {
        const modal = event.target.closest(MODAL_SELECTOR);
        if (!modal) return;

        updateModalRotateControls(modal);
    });

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.df-media-modal-rotate-button');
        if (!button) return;

        const modal = button.closest(MODAL_SELECTOR);
        if (modal?.dataset.dfMediaModalRotating === 'true') return;

        const trigger = activeModalMediaTrigger(modal);
        if (!mediaTriggerCanRotate(trigger)) return;

        event.preventDefault();
        currentDfMediaModalTrigger = trigger;
        setDfMediaModalRotating(modal, true);

        const degree = Number(button.dataset.degree);
        if (trigger.dataset.dfMediaType === 'image' && typeof window.rotateImage === 'function') {
            window.rotateImage(trigger, degree);
        } else if (trigger.dataset.dfMediaType === 'video' && typeof window.rotateVideo === 'function') {
            window.rotateVideo(trigger, degree);
        } else {
            setDfMediaModalRotating(modal, false);
        }
    });
};

const initializeDfMediaModal = () => {
    bindDfMediaModalEvents();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDfMediaModal);
} else {
    initializeDfMediaModal();
}

document.addEventListener('turbolinks:load', initializeDfMediaModal);

window.initializeDfMediaModal = initializeDfMediaModal;
window.refreshDfMediaModalFromTrigger = refreshDfMediaModalFromTrigger;
window.setDfMediaModalRotating = (isRotating) => setDfMediaModalRotating(document.querySelector(MODAL_SELECTOR), isRotating);
const MODAL_SELECTOR = '#df_media_modal';
const CONTENT_SELECTOR = '.df-media-modal-content';
const ROTATE_BUTTONS_SELECTOR = '.df-media-modal-rotate-buttons';
const ROTATE_SPINNER_SELECTOR = '.df-media-modal-rotate-spinner';
let dfMediaModalEventsBound = false;
let currentDfMediaModalTrigger = null;

const mediaItemsFromTrigger = (trigger) => {
    try {
        return JSON.parse(trigger.dataset.dfMediaItems || '[]');
    } catch (error) {
        return [];
    }
};

const imageElement = (item) => {
    const image = document.createElement('img');
    image.src = item.src;
    image.className = 'img-fluid w-100';
    if (item.alt) image.alt = item.alt;
    return image;
};

const videoElement = (item) => {
    const video = document.createElement('video');
    video.className = 'w-100';
    video.controls = true;
    video.playsInline = true;
    if (item.poster) video.poster = item.poster;

    const sources = item.sources?.length ? item.sources : [{ src: item.src, type: item.mimeType }];
    sources.forEach((source) => {
        if (!source.src) return;

        const element = document.createElement('source');
        element.src = source.src;
        if (source.type) element.type = source.type;
        video.appendChild(element);
    });

    return video;
};

const mediaElement = (item) => (item.type === 'video' ? videoElement(item) : imageElement(item));

const sliderElement = (items, activeIndex = 0) => {
    const carouselId = 'df_media_modal_carousel';
    const carousel = document.createElement('div');
    carousel.id = carouselId;
    carousel.className = 'carousel slide';
    carousel.dataset.bsRide = 'false';

    const inner = document.createElement('div');
    inner.className = 'carousel-inner';

    items.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = `carousel-item${index === activeIndex ? ' active' : ''}`;
        slide.appendChild(mediaElement(item));
        inner.appendChild(slide);
    });

    carousel.appendChild(inner);

    if (items.length > 1) {
        carousel.insertAdjacentHTML('beforeend',
            `<button class="btn apl-glas-effect carousel-control-prev rounded-circle" type="button" data-bs-target="#${carouselId}" data-bs-slide="prev">
                <i class="cww-icons ico-chevron-left fs-title-0" aria-hidden="true"></i>
                <span class="visually-hidden">Previous</span>
            </button>
            <button class="btn apl-glas-effect carousel-control-next rounded-circle" type="button" data-bs-target="#${carouselId}" data-bs-slide="next">
                <i class="cww-icons ico-chevron-right fs-title-0" aria-hidden="true"></i>
                <span class="visually-hidden">Next</span>
            </button>`
        );
    }

    return carousel;
};

const TRIGGER_SELECTOR = '[data-bs-target="#df_media_modal"][data-df-media-items]';
const GROUP_SELECTOR = '.files, .fb-element-dynamic-units, .form-row';
const groupedMediaItems = (trigger) => {
    const group = trigger.closest(GROUP_SELECTOR);
    const triggers = group ? Array.from(group.querySelectorAll(TRIGGER_SELECTOR)) : [trigger];

    const items = [];
    let activeIndex = 0;
    triggers.forEach((groupTrigger) => {
        const triggerItems = mediaItemsFromTrigger(groupTrigger);
        if (!triggerItems.length) return;
        if (groupTrigger === trigger) activeIndex = items.length;
        items.push(...triggerItems);
    });

    if (!items.length) {
        const fallback = mediaItemsFromTrigger(trigger);
        return { items: fallback, activeIndex: 0 };
    }

    return { items, activeIndex };
};

const renderDfMediaModal = (modal, trigger) => {
    const contentContainer = modal.querySelector(CONTENT_SELECTOR);
    if (!contentContainer) return;

    const { items, activeIndex } = trigger ? groupedMediaItems(trigger) : { items: [], activeIndex: 0 };

    contentContainer.replaceChildren();
    if (items.length === 0) return;

    contentContainer.appendChild(items.length === 1 ? mediaElement(items[0]) : sliderElement(items, activeIndex));
};

const activeModalMediaTrigger = (modal) => {
    if (!modal || !currentDfMediaModalTrigger) return null;

    const carousel = modal.querySelector('#df_media_modal_carousel');
    if (!carousel) return currentDfMediaModalTrigger;

    const activeItem = carousel.querySelector('.carousel-item.active');
    const activeIndex = Array.from(carousel.querySelectorAll('.carousel-item')).indexOf(activeItem);
    const group = currentDfMediaModalTrigger.closest(GROUP_SELECTOR);
    const triggers = group ? Array.from(group.querySelectorAll(TRIGGER_SELECTOR)) : [currentDfMediaModalTrigger];

    return triggers[activeIndex] || currentDfMediaModalTrigger;
};

const mediaTriggerCanRotate = (trigger) => (
    !!trigger
    && !!trigger.closest('.parent-block')?.querySelector('.rotate-buttons')
    && ['image', 'video'].includes(trigger.dataset.dfMediaType)
);

const updateModalRotateControls = (modal) => {
    const rotateButtons = modal.querySelector(ROTATE_BUTTONS_SELECTOR);
    const rotateSpinner = modal.querySelector(ROTATE_SPINNER_SELECTOR);
    if (!rotateButtons) return;

    const isRotating = modal.dataset.dfMediaModalRotating === 'true';
    rotateButtons.classList.toggle('d-none', isRotating || !mediaTriggerCanRotate(activeModalMediaTrigger(modal)));
    rotateButtons.querySelectorAll('button').forEach((button) => {
        button.disabled = isRotating;
    });
    if (rotateSpinner) rotateSpinner.classList.toggle('d-none', !isRotating);
};

const setDfMediaModalRotating = (modal, isRotating) => {
    if (!modal) return;

    modal.dataset.dfMediaModalRotating = isRotating ? 'true' : 'false';
    updateModalRotateControls(modal);
};

const refreshDfMediaModalFromTrigger = (trigger) => {
    const modal = document.querySelector(MODAL_SELECTOR);
    if (!modal || !modal.classList.contains('show')) return;

    currentDfMediaModalTrigger = trigger || currentDfMediaModalTrigger;
    renderDfMediaModal(modal, currentDfMediaModalTrigger);
    setDfMediaModalRotating(modal, false);
    updateModalRotateControls(modal);
};

const clearDfMediaModal = (modal) => {
    const contentContainer = modal.querySelector(CONTENT_SELECTOR);
    if (!contentContainer) return;

    contentContainer.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
    });
    contentContainer.replaceChildren();
    currentDfMediaModalTrigger = null;
};

const bindDfMediaModalEvents = () => {
    if (dfMediaModalEventsBound || window.dfMediaModalEventsBound) return;
    dfMediaModalEventsBound = true;
    window.dfMediaModalEventsBound = true;

    document.addEventListener('show.bs.modal', (event) => {
        if (!event.target.matches(MODAL_SELECTOR)) return;
        setDfMediaModalRotating(event.target, false);
        currentDfMediaModalTrigger = event.relatedTarget;
        renderDfMediaModal(event.target, currentDfMediaModalTrigger);
        updateModalRotateControls(event.target);
    });

    document.addEventListener('hidden.bs.modal', (event) => {
        if (!event.target.matches(MODAL_SELECTOR)) return;
        setDfMediaModalRotating(event.target, false);
        clearDfMediaModal(event.target);
    });

    document.addEventListener('slid.bs.carousel', (event) => {
        const modal = event.target.closest(MODAL_SELECTOR);
        if (!modal) return;

        updateModalRotateControls(modal);
    });

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.df-media-modal-rotate-button');
        if (!button) return;

        const modal = button.closest(MODAL_SELECTOR);
        if (modal?.dataset.dfMediaModalRotating === 'true') return;

        const trigger = activeModalMediaTrigger(modal);
        if (!mediaTriggerCanRotate(trigger)) return;

        event.preventDefault();
        currentDfMediaModalTrigger = trigger;
        setDfMediaModalRotating(modal, true);

        const degree = Number(button.dataset.degree);
        if (trigger.dataset.dfMediaType === 'image' && typeof window.rotateImage === 'function') {
            window.rotateImage(trigger, degree);
        } else if (trigger.dataset.dfMediaType === 'video' && typeof window.rotateVideo === 'function') {
            window.rotateVideo(trigger, degree);
        } else {
            setDfMediaModalRotating(modal, false);
        }
    });
};

const initializeDfMediaModal = () => {
    bindDfMediaModalEvents();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDfMediaModal);
} else {
    initializeDfMediaModal();
}

document.addEventListener('turbolinks:load', initializeDfMediaModal);

window.initializeDfMediaModal = initializeDfMediaModal;
window.refreshDfMediaModalFromTrigger = refreshDfMediaModalFromTrigger;
window.setDfMediaModalRotating = (isRotating) => setDfMediaModalRotating(document.querySelector(MODAL_SELECTOR), isRotating);
